import type { UntypedFormGroup } from '@angular/forms';
import { IDynamicModel, dynamicModelFormArrayTypes } from '../models';
import {
  dynamicRecordIdProperty,
  dynamicValuePropertyIsExplicit,
} from './dynamic-value-property';

/**
 * A list or picker can hand back its tap event (`{ item, index, view, ... }`) instead of the
 * record. The wrapper is truthy, so it passes a required check, yet it carries none of the
 * record's properties and serializes to nothing. Every path that stores or displays a
 * selection goes through this first.
 */
export function dynamicUnwrapSelection<T>(selection: T): T {
  const nested =
    selection && typeof selection === 'object'
      ? (selection as { item?: unknown }).item
      : null;
  return nested ? (nested as T) : selection;
}

/**
 * The id a record contributes, read from dynamicRecordIdProperty (the configured valueProperty,
 * or the Id/id fallback, Bug 35174). A primitive is already the id, so it is kept as-is: a
 * typeahead holds a bare scalar id until hydration replaces it with the record.
 */
function resolveRecordId(value: unknown, fieldControl: IDynamicModel): unknown {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  const key = dynamicRecordIdProperty(value, fieldControl);
  return key ? (value as Record<string, unknown>)[key] : undefined;
}

/**
 * The value a control contributes to the saved response for the form value it holds.
 * dynamicIsRequiredValueMissing applies the same record-id mapping, so the submit gate and
 * the persisted payload cannot disagree.
 */
export function dynamicSerializeControlValue(
  fieldControl: IDynamicModel,
  formValue: unknown
): unknown {
  const valueProperty = fieldControl?.valueProperty as string | undefined;
  let value = formValue;
  if (value && valueProperty) {
    if (Array.isArray(value)) {
      // TODO: discuss how multiple values are best represented in backend data
      value = value.map((v) => `${resolveRecordId(v, fieldControl)}`).join(',');
    } else {
      value = resolveRecordId(value, fieldControl);
    }
  }
  let serialized =
    fieldControl?.inputType === 'number' && !isNaN(+(value as number))
      ? +(value as number)
      : value;
  const formatted = (value as { formatted?: unknown } | null)?.formatted;
  if (fieldControl?.type === 'date' && value && formatted) {
    serialized = formatted;
  }
  return serialized;
}

function isEmptySavedValue(value: unknown): boolean {
  return (
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * True for controls whose value is a record (or records) identified by `valueProperty`.
 * `createConfigGroup` defaults `valueProperty` to the control's own `formControlName` for
 * EVERY control in the active model group, so a `valueProperty` on its own says nothing about
 * the value's shape; only the control type does.
 */
export function dynamicControlStoresRecords(
  fieldControl: IDynamicModel
): boolean {
  return (
    typeof fieldControl?.type === 'string' &&
    fieldControl.type.startsWith('typeahead')
  );
}

/**
 * True when a required control would be saved empty. Judged on what would be persisted, not
 * the form control alone: for a record-valued control, a tap-event wrapper, a record without
 * the control's `valueProperty`, or an array of such records is truthy at the control but
 * lands as NULL. Any other non-empty value (text, a date string, a `{ formatted }` date, a
 * boolean, zero, a bare id) is present. With only the formControlName default, a record is
 * judged by the Id/id it carries (Bug 35174); a record with neither is an unknown shape and
 * counts as present, as it did before 1.4.4 (Bug 35153).
 */
export function dynamicIsRequiredValueMissing(
  fieldControl: IDynamicModel,
  formValue: unknown
): boolean {
  if (isEmptySavedValue(formValue)) {
    return true;
  }
  const valueProperty = fieldControl?.valueProperty as string | undefined;
  if (!valueProperty || !dynamicControlStoresRecords(fieldControl)) {
    return false;
  }
  const explicit = dynamicValuePropertyIsExplicit(fieldControl);
  const records = Array.isArray(formValue) ? formValue : [formValue];
  return records.every((record) => {
    const key = dynamicRecordIdProperty(record, fieldControl);
    const unknownShape =
      record != null &&
      typeof record === 'object' &&
      !(key && Object.prototype.hasOwnProperty.call(record, key));
    if (!explicit && unknownShape) {
      return false;
    }
    return isEmptySavedValue(resolveRecordId(record, fieldControl));
  });
}

export function dynamicRequiredFieldsMessage(labels: Array<string>): string {
  return `To submit the form, please fill out these remaining fields: ${labels
    .map((l) => `'${l}'`)
    .join(', ')}.`;
}

/**
 * Labels of the required top-level controls whose value would be saved empty, read from the
 * live form immediately before the payload is built. A control that is empty yet reports no
 * `required` error had its requirement lifted at runtime (hidden or conditional) and is left to
 * the form's own validity. Form-array controls (accordions) have their own gate.
 */
export function dynamicRequiredControlLabelsSavingEmpty(
  displayControls: Array<IDynamicModel>,
  formGroup: UntypedFormGroup
): Array<string> {
  const labels: Array<string> = [];
  if (!displayControls || !formGroup) {
    return labels;
  }
  for (const control of displayControls) {
    if (
      dynamicModelFormArrayTypes.includes(control.type) ||
      !control.required ||
      control.ignoreValue ||
      control.options?.hidden ||
      !control.formControlName
    ) {
      continue;
    }
    const formControl = formGroup.get(control.formControlName);
    if (!formControl || formControl.disabled) {
      continue;
    }
    const value = formControl.value;
    if (isEmptySavedValue(value) && !formControl.hasError('required')) {
      continue;
    }
    if (dynamicIsRequiredValueMissing(control, value)) {
      labels.push(control.label || control.formControlName);
    }
  }
  return labels;
}

/** The remaining-fields message for a full submit that would persist a required control empty, or null when clean. */
export function dynamicRequiredSavingEmptyMessage(
  displayControls: Array<IDynamicModel>,
  formGroup: UntypedFormGroup
): string | null {
  const labels = dynamicRequiredControlLabelsSavingEmpty(
    displayControls,
    formGroup
  );
  return labels.length ? dynamicRequiredFieldsMessage(labels) : null;
}

/**
 * The control-level required check that runs when an accordion-gated form passes its accordion
 * conditions: every required non-accordion control must hold a value. It keeps that gate's
 * legacy strictness (a boolean false or a zero counts as empty here, unlike Validators.required)
 * and adds the saved-empty rule, so a truthy value that would persist as NULL is caught at the
 * same point as an empty one. Returns the remaining-fields message, or null when satisfied.
 */
export function dynamicRequiredControlsBlockingMessage(
  controls: Array<IDynamicModel>,
  formGroup: UntypedFormGroup
): string | null {
  const labels: Array<string> = [];
  for (const control of controls ?? []) {
    if (control.type === 'accordion' || !control.required) {
      continue;
    }
    const formControl = control.formControlName
      ? formGroup?.get(control.formControlName)
      : null;
    if (
      formControl &&
      (!formControl.value ||
        dynamicIsRequiredValueMissing(control, formControl.value))
    ) {
      labels.push(control.label);
    }
  }
  return labels.length ? dynamicRequiredFieldsMessage(labels) : null;
}

/**
 * WI 34928: a multi-select selection with `pick` added once. With a `key` an equal record (same
 * `key` value) counts as already picked; without one only the same reference does. A new array is
 * returned, so the caller can write it straight to the form control.
 */
export function dynamicWithPick<T>(
  items: ReadonlyArray<T> | null | undefined,
  pick: T,
  key?: string | null
): Array<T> {
  const current = [...(items ?? [])];
  const id = (record: T) => (record as Record<string, unknown>)?.[key ?? ''];
  const picked = current.some((item) =>
    key ? id(item) != null && `${id(item)}` === `${id(pick)}` : item === pick
  );
  return picked ? current : [...current, pick];
}

/**
 * WI 34928: true when two multi-select records are the same option: the same reference, or equal
 * ids read the way the save reads them (dynamicRecordIdProperty, so a formControlName default
 * falls back to Id). Ids compare as text, because a Bug 34978 placeholder holds its saved id as a
 * string while a search returns a number. With no id on either record, `fallbackProperty` (the
 * chip's display property) decides, so a control whose valueProperty no record carries can still
 * remove a chip.
 */
export function dynamicSameRecord(
  a: unknown,
  b: unknown,
  model: Parameters<typeof dynamicRecordIdProperty>[1],
  fallbackProperty?: string
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  const field = (record: object, key?: string) =>
    key ? (record as Record<string, unknown>)[key] : undefined;
  const idA = field(a, dynamicRecordIdProperty(a, model));
  const idB = field(b, dynamicRecordIdProperty(b, model));
  if (idA != null && idB != null) {
    return `${idA}` === `${idB}`;
  }
  if (idA != null || idB != null || !fallbackProperty) {
    return false;
  }
  return field(a, fallbackProperty) === field(b, fallbackProperty);
}
